import * as React from "react";
import { useAsync } from "react-use";
import { useApi } from "../../../../containers/ApiContainer";
import { Aircraft } from "../../../../types";
import { AircraftWithHistory } from "../aircraftSuggestionUtils";
import { CsvMatcher } from "../csvMatcher";
import { FAAMapping } from "../types";

/**
 * Hook for fetching aircraft with historical FAA mapping data.
 *
 * This hook automatically:
 * 1. Fetches existing FAA mappings from the database (web.get_faa_mappings RPC)
 * 2. Fetches all aircraft from the aircraft table
 * 3. Groups FAA strings by aircraft to build "alreadyMatched" history
 * 4. Returns enriched aircraft list for use with getSuggestedAircraft()
 *
 * The hook is self-learning: as users add more manual mappings to web.faa_to_airplx,
 * the suggestions automatically improve on the next page load.
 *
 * @returns Object with aircraftWithHistory array and loading state
 */
export const useFAAMappingSuggestions = () => {
  const { postgrest } = useApi();

  // Fetch existing FAA mappings (the "training data" for suggestions)
  const { value: existingMappings, loading: loadingMappings } = useAsync(
    async () => {
      const { data, error } = await postgrest.rpc("get_faa_mappings");

      if (error) {
        console.error("Failed to fetch FAA mappings:", error);
        return [];
      }

      return (data as FAAMapping[]) || [];
    },
    [postgrest]
  );

  // Fetch all aircraft for suggestions
  const { value: allAircraft, loading: loadingAircraft } = useAsync(
    async () => {
      const result = await postgrest
        .from("aircraft")
        .select("id,make,model,geom,linked_to")
        .order("make")
        .order("model");

      return (
        result.body?.filter((row: Aircraft) => row.geom || row.linked_to) || []
      );
    },
    [postgrest]
  );

  // Build AircraftWithHistory by enriching aircraft with historical FAA mappings
  const aircraftWithHistory = React.useMemo<AircraftWithHistory[]>(() => {
    if (!allAircraft || !existingMappings) {
      return [];
    }

    // Group FAA strings by matched_aircraft_id
    // Map<aircraft_id, string[]> where strings are "MANUFACTURER / MODEL"
    const historyMap = new Map<string, string[]>();

    existingMappings.forEach((mapping) => {
      if (!mapping.matched_aircraft_id) return;

      const faaString = `${mapping.faa_manufacturer} / ${mapping.faa_model}`;
      const existing = historyMap.get(mapping.matched_aircraft_id) || [];

      // Avoid duplicates
      if (!existing.includes(faaString)) {
        existing.push(faaString);
        historyMap.set(mapping.matched_aircraft_id, existing);
      }
    });

    // Enrich each aircraft with its historical mappings
    return allAircraft.map((aircraft) => ({
      ...aircraft,
      alreadyMatched: historyMap.get(aircraft.id) || [],
    }));
  }, [allAircraft, existingMappings]);

  // Build CSV matcher from existing mappings for verified exact matches
  const csvMatcher = React.useMemo(() => {
    if (!existingMappings || existingMappings.length === 0) {
      return null;
    }

    return CsvMatcher.fromRows(existingMappings);
  }, [existingMappings]);

  return {
    aircraftWithHistory,
    csvMatcher,
    loading: loadingMappings || loadingAircraft,
  };
};
